/**
 * Hard limits and defaults shared by client, server and the single-player worker. The server
 * enforces them (validate.ts, apps/server); the UI uses them for `maxLength` & friends so a
 * well-behaved client never produces input the server rejects.
 */
import { MAX_FACTIONS } from '@friction/engine';
import type { RoomSettings } from './messages';

export const LIMITS = {
  /** Largest client → server WebSocket frame the server accepts, in bytes. */
  maxFrameBytes: 64 * 1024,
  /** Player (commander) name, in characters after sanitising. */
  playerName: 20,
  roomName: 32,
  chatText: 200,
  opName: 32,
  /** Units addressed by a single order command. */
  orderUnits: 256,
  /** Lobby slots per room (= factions per match). */
  slots: MAX_FACTIONS,
  /** Teams are numbered 0..teams-1. */
  teams: MAX_FACTIONS,
  maxDayLimit: 365,
  /** Upper bound for hex / city / tick ids in commands (engine validates the real range). */
  maxHexId: 0xffff,
  maxCityId: 0xffff,
  maxTick: 10_000_000,
  /** Reconnect tokens are base64url strings of this maximum length. */
  tokenLength: 64,
  /**
   * Replay files (the film archive). The longest war the lobby allows (large, 6 factions, a
   * 365-day limit) is ≈ 1.7 MB of msgpack, ≈ 0.4 MB gzipped; the caps leave room for long
   * unlimited wars while bounding what an untrusted file can make the client decompress and
   * re-simulate.
   */
  replayFileBytes: 8 * 1024 * 1024,
  replayPayloadBytes: 32 * 1024 * 1024,
  /** 1000 days of war. */
  replayTicks: 24_000,
  replayEntries: 1_000_000,
  /** Commands in one replay entry. */
  replayBatch: 10_000,
} as const;

/** Room codes: 5 letters without the ambiguous I, L and O. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 5;
export const ROOM_CODE_PATTERN = /^[ABCDEFGHJKMNPQRSTUVWXYZ]{5}$/;

/** Normalise user-typed room codes ("  abcde " → "ABCDE"). Does not validate. */
export function normalizeRoomCode(raw: string): string {
  return raw.trim().toUpperCase();
}

export const DEFAULT_ROOM_SETTINGS: Readonly<RoomSettings> = Object.freeze({
  name: 'War Room',
  mapSize: 'medium',
  seed: 0,
  dayLimit: 0,
  vpVictoryShare: 0.6,
  speed: 2,
  startingTechs: [],
  season: 'random',
  scenario: null,
  theatre: null,
});

/**
 * WebSocket close codes used by the server. Codes in FATAL_CLOSE_CODES mean "do not
 * auto-reconnect" — reconnecting would fail the same way (or fight another tab).
 */
export const CloseCode = {
  normal: 1000,
  /** Server shutting down or restarting — reconnect with backoff. */
  goingAway: 1001,
  policy: 1008,
  tooBig: 1009,
  version: 4000,
  /** The same identity connected from another tab/device. */
  replaced: 4001,
  helloTimeout: 4002,
  rateLimited: 4003,
  serverFull: 4004,
} as const;

export const FATAL_CLOSE_CODES: ReadonlySet<number> = new Set([
  CloseCode.policy,
  CloseCode.tooBig,
  CloseCode.version,
  CloseCode.replaced,
  CloseCode.rateLimited,
]);
